import type { Action, R1Detail } from '@cockpit/shared';
import { defineStore } from 'pinia';
import { computed, ref, shallowRef } from 'vue';
import { setOfflineLayer } from '../api/client';
import { KEPT_ON_WIPE, cacheKey, isCacheable } from '../offline/cache';
import {
  enqueue,
  nextRunnable,
  nextSeq,
  overlayActions,
  overlayDone,
  overlayR1,
  pendingTargets,
  sentText,
  targetOf,
  type ActionUpdateOp,
  type QueuedOp,
  type R1PrepOp,
} from '../offline/ops';
import { createSender, replayQueue, type ReplayReport, type SendFn } from '../offline/replay';
import { idbStorage, memoryStorage, type OfflineStorage } from '../offline/storage';
import { useToastStore } from './toast';

/** Intervalle des essais de reconnexion et de l'envoi périodique (ms). */
const CHECK_MS = 15_000;
const OWNER_KEY = 'owner';
/** Toast des gestes qui touchent l'extérieur, sans réseau. */
export const NET_ONLY_TOAST = 'Disponible avec le réseau : rien ne part vers l’extérieur sans réseau.';
/** Déconnexion faite sans réseau : la session du serveur sera fermée au retour du réseau. */
export const LOGOUT_PENDING_KEY = 'cockpit.logoutPending';

type OpPatch = Omit<QueuedOp, 'id' | 'seq' | 'userId' | 'createdAt' | 'state'>;

/**
 * Lot AW « Hors connexion sur mobile » : état du réseau, données gardées sur le téléphone et changements en
 * attente d'envoi.
 * - lectures de la liste blanche gardées (IndexedDB) et servies sans réseau, avec l'heure des données ;
 * - changements internes gardés dans l'ordre, montrés tout de suite, envoyés au retour du réseau (événement
 *   `online`, essai toutes les 15 s, démarrage de l'appli), conflits et refus à trancher par la personne ;
 * - tout est effacé à la déconnexion et quand une autre personne se connecte sur l'appareil.
 */
export const useOfflineStore = defineStore('offline', () => {
  let storage: OfflineStorage = memoryStorage();
  let send: SendFn = createSender({ get: (id) => storage.getBlob(id) });
  const owner = ref<string | null>(null);
  const ops = shallowRef<QueuedOp[]>([]);
  const browserOnline = ref(typeof navigator === 'undefined' ? true : navigator.onLine !== false);
  const reachable = ref(true);
  const online = computed(() => browserOnline.value && reachable.value);
  /** Heure des plus anciennes données gardées affichées depuis la perte du réseau (bandeau). */
  const dataAt = ref<string | null>(null);
  /** Heure de la dernière lecture gardée (les données à l'écran quand le réseau tombe). */
  const lastSavedAt = ref<string | null>(null);
  /** Heure affichée dans le bandeau : données servies depuis le téléphone, sinon dernière lecture réussie. */
  const shownDataAt = computed(() => dataAt.value ?? lastSavedAt.value);
  const replaying = ref(false);
  /** Augmente à chaque retour du réseau (les écrans rechargent leurs données). */
  const reconnects = ref(0);
  /** Changements envoyés depuis le dernier retour du réseau (ligne « Connecté · à jour à 15 h 06 »). */
  const syncedAt = ref<string | null>(null);
  const sessionExpired = ref(false);
  let installed = false;
  let timer: ReturnType<typeof setInterval> | undefined;
  let running: Promise<ReplayReport | null> | null = null;

  const pending = computed(() => ops.value.filter((o) => o.state === 'pending'));
  const conflicts = computed(() => ops.value.filter((o) => o.state === 'conflict'));
  const failed = computed(() => ops.value.filter((o) => o.state === 'failed'));
  const targets = computed(() => pendingTargets(ops.value));

  function sorted(list: QueuedOp[]): QueuedOp[] {
    return [...list].sort((a, b) => a.seq - b.seq);
  }

  // ---------------------------------------------------------------- Couche de l'API

  function transform(path: string, data: unknown): unknown {
    if (ops.value.length === 0) return data;
    const [pathname = '', query = ''] = path.split('?');
    const now = new Date().toISOString();
    if (pathname === '/api/actions' && Array.isArray(data)) {
      const params = new URLSearchParams(query);
      const status = params.get('status') ?? 'open';
      if (status !== 'open') return data;
      const who = params.get('who') ?? 'all';
      const list = overlayActions(data as Action[], ops.value, now);
      if (who === 'all') return list;
      const id = who === 'me' ? owner.value : who;
      return list.filter((a) => a.assigneeId === id);
    }
    if (pathname === '/api/actions/done' && Array.isArray(data))
      return overlayDone(data as Action[], ops.value);
    if (/^\/api\/r1\/[^/]+$/.test(pathname) && data && typeof data === 'object') {
      return overlayR1(data as R1Detail, ops.value);
    }
    return data;
  }

  function served(_path: string, savedAt: string): void {
    if (!dataAt.value || savedAt < dataAt.value) dataAt.value = savedAt;
  }

  function goneOffline(): void {
    reachable.value = false;
  }

  function cameBack(): void {
    const was = online.value;
    reachable.value = true;
    if (!was && online.value) onReconnect();
  }

  function onReconnect(): void {
    dataAt.value = null;
    reconnects.value += 1;
    void replay();
  }

  function install(options: { storage?: OfflineStorage | null; send?: SendFn } = {}): void {
    storage = options.storage ?? idbStorage() ?? memoryStorage();
    send = options.send ?? createSender({ get: (id) => storage.getBlob(id) });
    setOfflineLayer({
      isOffline: () => !online.value,
      isCacheable,
      read: async (path) => {
        const key = cacheKey(path, owner.value);
        if (!key) return null;
        const entry = await storage.getResponse(key);
        return entry ? { data: entry.data, savedAt: entry.savedAt } : null;
      },
      write: (path, data) => {
        const key = cacheKey(path, owner.value);
        const savedAt = new Date().toISOString();
        if (key) void storage.putResponse({ key, path, data, savedAt });
        lastSavedAt.value = savedAt;
      },
      served,
      networkDown: goneOffline,
      networkUp: cameBack,
      transform,
    });
    if (installed || typeof window === 'undefined') return;
    installed = true;
    window.addEventListener('online', () => {
      browserOnline.value = true;
      reachable.value = true;
      onReconnect();
    });
    window.addEventListener('offline', () => {
      browserOnline.value = false;
    });
    timer = setInterval(() => void check(), CHECK_MS);
  }

  /** Essai périodique : réseau revenu (le serveur répond) ? changements à envoyer ? */
  async function check(): Promise<void> {
    if (!browserOnline.value) return;
    if (!reachable.value) {
      try {
        const res = await fetch('/api/health', { cache: 'no-store' });
        if (res.ok) cameBack();
      } catch {
        // toujours pas de réseau
      }
      return;
    }
    if (pending.value.length > 0) void replay();
  }

  /** Garde une réponse obtenue autrement que par sa lecture (personne connectée rendue par la connexion). */
  async function remember(path: string, data: unknown): Promise<void> {
    const key = cacheKey(path, owner.value);
    if (key) await storage.putResponse({ key, path, data, savedAt: new Date().toISOString() });
  }

  function stop(): void {
    clearInterval(timer);
    timer = undefined;
  }

  // ---------------------------------------------------------------- Personne connectée

  /**
   * La personne connectée est connue : ses changements en attente sont relus ; si une autre personne était
   * connectée sur cet appareil, tout ce qu'elle avait gardé est effacé.
   */
  async function setUser(userId: string): Promise<void> {
    const previous = await storage.getMeta<string>(OWNER_KEY);
    if (previous && previous !== userId) await storage.clear(KEPT_ON_WIPE);
    await storage.setMeta(OWNER_KEY, userId);
    owner.value = userId;
    ops.value = sorted((await storage.listOps()).filter((o) => o.userId === userId));
    if (online.value && pending.value.length > 0) void replay();
  }

  /** Déconnexion : tout ce qui est gardé sur le téléphone est effacé (sauf la configuration de connexion). */
  async function wipe(): Promise<void> {
    await storage.clear(KEPT_ON_WIPE);
    owner.value = null;
    ops.value = [];
    dataAt.value = null;
    lastSavedAt.value = null;
    syncedAt.value = null;
    sessionExpired.value = false;
  }

  // ---------------------------------------------------------------- File des changements

  function hasQueued(target: string): boolean {
    return ops.value.some((o) => targetOf(o) === target);
  }

  /** Faut-il garder ce changement plutôt que l'envoyer ? (pas de réseau, ou des changements plus anciens sur la cible) */
  function mustQueue(target: string): boolean {
    return !online.value || hasQueued(target);
  }

  /** Garde un changement ; `id` = clé d'idempotence déjà utilisée par un envoi direct qui a perdu le réseau. */
  async function add(patch: OpPatch, id: string = crypto.randomUUID()): Promise<QueuedOp> {
    const op = {
      ...patch,
      id,
      seq: nextSeq(ops.value),
      userId: owner.value ?? '',
      createdAt: new Date().toISOString(),
      state: 'pending',
    } as QueuedOp;
    const result = enqueue(ops.value, op);
    ops.value = result.ops;
    for (const saved of result.saved) await storage.putOp(saved);
    for (const id of result.removed) await storage.deleteOp(id);
    if (online.value) void replay();
    return result.saved[0] ?? op;
  }

  async function putBlob(blob: Blob): Promise<string> {
    const id = crypto.randomUUID();
    await storage.putBlob(id, blob);
    return id;
  }

  async function saveOp(op: QueuedOp): Promise<void> {
    ops.value = sorted([...ops.value.filter((o) => o.id !== op.id), op]);
    await storage.putOp(op);
  }

  async function removeOp(op: QueuedOp): Promise<void> {
    ops.value = ops.value.filter((o) => o.id !== op.id);
    await storage.deleteOp(op.id);
    if (op.kind === 'feedback.create') for (const id of op.blobIds) await storage.deleteBlob(id);
  }

  /** Envoie la file (une seule fois à la fois) ; toast « 3 changements envoyés · 1 à trancher ». */
  function replay(): Promise<ReplayReport | null> {
    if (running) return running;
    if (!online.value || !owner.value || !nextRunnable(ops.value)) return Promise.resolve(null);
    replaying.value = true;
    let again = false;
    running = (async () => {
      try {
        const report = await replayQueue({ list: () => ops.value, save: saveOp, remove: removeOp }, send);
        if (report.stopped === 'offline') goneOffline();
        sessionExpired.value = report.stopped === 'unauthenticated';
        const text = sentText(report.sent, report.conflicts, report.failed);
        if (text) useToastStore().show(text, 5000);
        if (report.sent > 0) {
          syncedAt.value = new Date().toISOString();
          reconnects.value += 1;
        }
        again = report.stopped === null;
        return report;
      } catch {
        return null;
      } finally {
        replaying.value = false;
        running = null;
        // Des changements arrivés pendant l'envoi partent à leur tour.
        if (again && online.value && nextRunnable(ops.value)) setTimeout(() => void replay(), 0);
      }
    })();
    return running;
  }

  /** « Réessayer » un changement refusé. */
  async function retry(id: string): Promise<void> {
    const op = ops.value.find((o) => o.id === id);
    if (!op) return;
    const { error: _error, ...rest } = op;
    await saveOp({ ...rest, state: 'pending' } as QueuedOp);
    void replay();
  }

  /** « Abandonner » un changement (refusé ou en conflit) : il est oublié, l'écran se recharge. */
  async function abandon(id: string): Promise<void> {
    const op = ops.value.find((o) => o.id === id);
    if (!op) return;
    await removeOp(op);
    reconnects.value += 1;
  }

  /**
   * Conflit tranché. « Garder ta version » : ton changement repart sur la version actuelle (choix explicite).
   * « Garder la sienne » : les champs en conflit sont retirés de ton changement ; le reste part, sinon il est oublié.
   */
  async function resolve(id: string, keep: 'mine' | 'theirs'): Promise<void> {
    const op = ops.value.find((o) => o.id === id);
    if (!op || op.state !== 'conflict') return;
    if (op.kind === 'action.update' && op.conflict) {
      const { conflict, ...rest } = op;
      const base = { ...rest.base, updatedAt: conflict.current.updatedAt };
      if (keep === 'mine') {
        await saveOp({ ...rest, base, state: 'pending' } satisfies ActionUpdateOp);
      } else {
        const dropped = new Set(conflict.fields.map((f) => f.field));
        const body = Object.fromEntries(Object.entries(rest.body).filter(([k]) => !dropped.has(k)));
        if (Object.keys(body).length === 0) await removeOp(op);
        else
          await saveOp({
            ...rest,
            body: body as ActionUpdateOp['body'],
            base,
            state: 'pending',
          } satisfies ActionUpdateOp);
      }
    } else if (op.kind === 'r1.prep' && op.conflict) {
      const { conflict, ...rest } = op;
      const base = { ...rest.base, filledAt: conflict.current.prep?.filledAt ?? null };
      if (keep === 'mine' || !('fields' in rest.input)) {
        if (keep === 'theirs') await removeOp(op);
        else await saveOp({ ...rest, base, state: 'pending' } satisfies R1PrepOp);
      } else {
        const dropped = new Set(conflict.fields.map((f) => f.field));
        const fields = Object.fromEntries(Object.entries(rest.input.fields).filter(([k]) => !dropped.has(k)));
        if (Object.keys(fields).length === 0) await removeOp(op);
        else await saveOp({ ...rest, input: { fields }, base, state: 'pending' } satisfies R1PrepOp);
      }
    }
    reconnects.value += 1;
    void replay();
  }

  return {
    owner,
    ops,
    online,
    browserOnline,
    reachable,
    dataAt,
    shownDataAt,
    replaying,
    reconnects,
    syncedAt,
    sessionExpired,
    pending,
    conflicts,
    failed,
    targets,
    install,
    stop,
    check,
    remember,
    setUser,
    wipe,
    mustQueue,
    hasQueued,
    add,
    putBlob,
    replay,
    retry,
    abandon,
    resolve,
    goneOffline,
    cameBack,
  };
});
